import { isEmpty, isNil } from 'lodash';
import React, { Key, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RCMenu, RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { ArchiveRow } from '@shared/components/list';
import { MBLoadingIndicator } from '@shared/components/loading';
import { TextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { ReferencedInComponent, componentTemplateOptions } from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { useSetComponentField } from '@shared/scenes/workflows/edit/hooks';
import { WorkspaceDetail } from '@shared/scenes/workflows/hooks';
import { OptionsData } from '@shared/scenes/workflows/template/options/OptionsData';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { NestedOptionsRow } from '../NestedOptionsRow';
import { Divider } from '../divider';
import { SelectableItemList } from './SelectableItemList';
import { useReferencedInOptions } from './useReferencedInOptions';

interface Props {
  component: MBAPI.WorkflowTemplate_components_ReferencedInComponent;
  workflowTemplateId: string;
  workspaceDetails: Map<string, WorkspaceDetail>;
}

export function ReferencedInOptions({ component, workflowTemplateId, workspaceDetails }: Props) {
  const colors = useContext(ColorThemeContext);
  const [openKeys, setOpenKeys] = useState<Key[]>([]);
  const [label] = useSetComponentField<string, ReferencedInComponent>(component, 'label');

  const {
    selectedWorkflowTemplate,
    setSelectedWorkflowId,
    selectedComponent,
    setSelectedComponentId,
    selectableWorkflowItems,
    selectableComponentItems,
    workflowLoading,
    viewTemplatesLoading,
    selectedViewTemplate,
    selectableViewTemplates,
    setSelectedViewTemplate,
  } = useReferencedInOptions(workflowTemplateId, workspaceDetails, component);

  const selectedWorkspaceName = selectedWorkflowTemplate
    ? workspaceDetails.get(selectedWorkflowTemplate.workflowTemplateId)?.name
    : undefined;

  const dispatch = useWorkflowDispatch();
  const onArchive = useCallback(() => {
    dispatch({
      type: 'archiveComponent',
      args: component.id,
    });
  }, [component.id, dispatch]);

  const optionsData = useMemo<OptionsData>(
    () => componentTemplateOptions.optionsDataForComponent(component),
    [component],
  );

  const updateLabel = useCallback(
    (value: string) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          label: value,
        },
      });
    },
    [dispatch, component],
  );

  useEffect(() => {
    if (!isNil(selectedWorkflowTemplate) && isNil(component.label)) {
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          label: `${selectedWorkflowTemplate.name}`,
        },
      });
    }
  }, [selectedWorkflowTemplate, selectedComponent, component, dispatch]);

  return (
    <View>
      <RCMenu
        mode="vertical"
        openKeys={openKeys as string[]}
        onOpenChange={setOpenKeys}
        triggerSubMenuAction="click"
        style={{
          backgroundColor: 'white',
          borderRadius: grid(1),
          boxShadow: '0 0 10px #d9d9d9',
          maxHeight: '100vh',
          overflow: 'auto',
        }}
        expandIcon={null}
      >
        <View paddingHorizontal={3}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.fieldType')}
          </MBTextOverlineBold>
        </View>
        <View paddingHorizontal={3} paddingVertical={2} row centerVertically>
          <MBIconV2 color={colors.foregroundActive} name={optionsData.icon} size={grid(3)} />
          <View paddingLeft={1} />
          <MBTextBody1 color={colors.foregroundActive}>{optionsData.title}</MBTextBody1>
        </View>
        <View paddingHorizontal={2}>
          <Divider />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.label')}
          </MBTextOverlineBold>
          <View padding={1} />
          <TextInput autosize={true} value={label} onChangeText={updateLabel} />
        </View>
        <View paddingHorizontal={2} paddingTop={2}>
          <Divider />
        </View>
        {workflowLoading && <MBLoadingIndicator />}
        {!workflowLoading && (
          <>
            <View paddingHorizontal={3} paddingVertical={1}>
              <MBTextOverlineBold color={colors.foregroundInactive}>
                {i18n('builder.referencedInOptions.referentialWorkflow')}
              </MBTextOverlineBold>
            </View>
            <RCSubMenu
              key="workflowRow"
              style={{ zIndex: 999 }}
              title={
                <NestedOptionsRow
                  name={selectedWorkspaceName ?? i18n('builder.referencedInOptions.selectWorkflow')}
                  defaultIcon={'duplicate-squares'}
                  selectedIcon={'duplicate-squares'}
                  imageUrl={selectedWorkflowTemplate?.imageUrl}
                  enabled={true}
                />
              }
            >
              <View paddingVertical={1}>
                <SelectableItemList
                  items={selectableWorkflowItems}
                  onSelectItem={(id: string) => {
                    if (id === selectedWorkflowTemplate?.workflowTemplateId) {
                      setSelectedWorkflowId(undefined);
                    } else {
                      setSelectedWorkflowId(id);
                    }
                  }}
                  enabled={true}
                  selectedItem={selectedWorkflowTemplate?.workflowTemplateId}
                />
              </View>
            </RCSubMenu>
          </>
        )}
        {!workflowLoading && !isNil(selectedWorkflowTemplate) && (
          <>
            <View paddingHorizontal={3} paddingVertical={1}>
              <MBTextOverlineBold color={colors.foregroundInactive}>
                {i18n('builder.referencedInOptions.referentialComponent')}
              </MBTextOverlineBold>
            </View>
            <RCSubMenu
              key="componentRow"
              style={{ zIndex: 999 }}
              title={
                <NestedOptionsRow
                  name={
                    selectedComponent?.label ?? i18n('builder.referencedInOptions.selectComponent')
                  }
                  defaultIcon={(selectedComponent?.icon as MBIconV2Name) ?? 'duplicate-squares'}
                  selectedIcon={(selectedComponent?.icon as MBIconV2Name) ?? 'duplicate-squares'}
                  enabled={true}
                />
              }
            >
              <View paddingVertical={1}>
                <SelectableItemList
                  items={selectableComponentItems}
                  onSelectItem={(id: string) => {
                    if (id === selectedComponent?.id) {
                      setSelectedComponentId(undefined);
                    } else {
                      setSelectedComponentId(id);
                    }
                  }}
                  enabled={true}
                  selectedItem={selectedComponent?.id}
                />
              </View>
            </RCSubMenu>
          </>
        )}
        {!viewTemplatesLoading && !isNil(selectedWorkflowTemplate) && (
          <>
            <View paddingHorizontal={3} paddingVertical={1}>
              <MBTextOverlineBold color={colors.foregroundInactive}>
                {i18n('builder.referencedInOptions.referentialViewTemplate')}
              </MBTextOverlineBold>
            </View>
            <RCSubMenu
              key="viewRow"
              style={{ zIndex: 999 }}
              title={
                <NestedOptionsRow
                  name={
                    selectedViewTemplate?.label ?? i18n('builder.referencedInOptions.selectView')
                  }
                  defaultIcon={(selectedViewTemplate?.icon as MBIconV2Name) ?? 'list'}
                  selectedIcon={(selectedViewTemplate?.icon as MBIconV2Name) ?? 'list'}
                  enabled={true}
                />
              }
            >
              <>
                {isEmpty(selectableViewTemplates) && (
                  <View padding={2}>
                    <MBTextBody1 style={{ textAlign: 'center' }}>
                      {i18n('builder.referencedInOptions.emptyList', {
                        workflowName: selectedWorkspaceName,
                      })}
                    </MBTextBody1>
                  </View>
                )}
                {!isEmpty(selectableViewTemplates) && (
                  <View paddingVertical={1}>
                    <SelectableItemList
                      items={selectableViewTemplates}
                      onSelectItem={(id: string) => {
                        if (id === selectedViewTemplate?.id) {
                          setSelectedViewTemplate(undefined);
                        } else {
                          setSelectedViewTemplate(id);
                        }
                      }}
                      enabled={true}
                      selectedItem={selectedViewTemplate?.id}
                    />
                  </View>
                )}
              </>
            </RCSubMenu>
          </>
        )}
        <View paddingHorizontal={2} paddingTop={1}>
          <Divider />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.options')}
          </MBTextOverlineBold>
        </View>
        <ArchiveRow disabled={!!component.locked} onArchive={onArchive} />
      </RCMenu>
    </View>
  );
}
